/**
 * ULID：26 字符 Crockford Base32，字典序即时间序。
 *
 * 前 10 字符 = 48 位毫秒时间戳，后 16 字符 = 80 位随机。
 * 不引三方依赖（architecture §13：shared 零运行时依赖）。
 *
 * 随机部分用 CSPRNG（globalThis.crypto）。shared 是平台中立包，
 * 不能 import node:crypto —— Node 18+ 与所有现代浏览器都提供全局 crypto。
 */
import type { Millis, Ulid } from './ids.js'

/** Crockford Base32：去掉了易混淆的 I / L / O / U */
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const ENCODING_LEN = 32
const TIME_LEN = 10
const RANDOM_LEN = 16
export const ULID_LEN = TIME_LEN + RANDOM_LEN

/** 48 位时间戳的上限，超过则无法用 10 个 Base32 字符表示 */
export const ULID_MAX_TIME = 281_474_976_710_655 // 2^48 - 1

export class InvalidUlidError extends Error {
  constructor(value: string) {
    super(`Invalid ULID: ${JSON.stringify(value)}`)
    this.name = 'InvalidUlidError'
  }
}

function encodeTime(time: Millis): string {
  if (!Number.isInteger(time) || time < 0 || time > ULID_MAX_TIME) {
    throw new RangeError(`ULID time out of range: ${time}`)
  }
  let out = ''
  let t = time
  for (let i = 0; i < TIME_LEN; i++) {
    const mod = t % ENCODING_LEN
    out = ENCODING[mod]! + out
    t = (t - mod) / ENCODING_LEN
  }
  return out
}

/**
 * 80 位随机 → 16 个 Base32 字符。
 * 10 字节恰好 = 16 × 5 bit，无填充浪费，也就没有分布偏斜。
 */
function encodeRandom(randomBytes: Uint8Array): string {
  let out = ''
  let bits = 0
  let bitCount = 0
  for (let i = 0; i < randomBytes.length; i++) {
    bits = (bits << 8) | randomBytes[i]!
    bitCount += 8
    while (bitCount >= 5) {
      bitCount -= 5
      out += ENCODING[(bits >>> bitCount) & 31]!
      bits &= (1 << bitCount) - 1
    }
  }
  return out
}

/**
 * 只声明我们用到的那一个方法。
 * shared 的 lib 是纯 ES2022（不含 DOM，也不含 node 类型）以保持平台中立，
 * 所以这里不能直接用 globalThis.crypto 的内置类型。
 */
interface MinimalCrypto {
  getRandomValues<T extends ArrayBufferView>(array: T): T
}

function cryptoRandomBytes(n: number): Uint8Array {
  const c = (globalThis as { crypto?: MinimalCrypto }).crypto
  if (!c?.getRandomValues) {
    throw new Error('globalThis.crypto.getRandomValues 不可用，无法安全生成 ULID')
  }
  return c.getRandomValues(new Uint8Array(n))
}

/**
 * 生成一个 ULID。
 * @param time 毫秒时间戳。引擎是纯函数不得调 Date.now()（硬性规则 #16），
 *             所以时间必须由调用方注入；只有 I/O 边界层可以传 Date.now()。
 */
export function ulid(time: Millis): Ulid {
  return encodeTime(time) + encodeRandom(cryptoRandomBytes(10))
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/

export function isValidUlid(s: string): boolean {
  // 首字符 > '7' 时时间戳溢出 48 位
  return ULID_RE.test(s) && s[0]! <= '7'
}

/** 取出 ULID 的时间戳部分。非法输入抛 InvalidUlidError。 */
export function ulidTime(s: Ulid): Millis {
  if (!isValidUlid(s)) throw new InvalidUlidError(s)
  let t = 0
  for (let i = 0; i < TIME_LEN; i++) {
    t = t * ENCODING_LEN + ENCODING.indexOf(s[i]!)
  }
  return t
}
